import {gql} from '@apollo/client';

import {Permissions} from 'auth/PermissionConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LocationViewQuery} from 'samples/locations/__generated/LocationViewQuery';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

type LocationViewQueryLocation = ExtractByTypename<LocationViewQuery['location'], 'LocationNode'>;

type LocationViewQueryStorableSchema = LocationViewQueryLocation['storableSchema'];

type LocationViewQueryParentStorableSchema = ExtractByTypename<
  LocationViewQueryLocation['parentStorable'],
  'BoxNode'
>['storableSchema'];

type LocationViewQueryParentStorable = ExtractByTypename<
  LocationViewQueryLocation['parentStorable'],
  'BoxNode'
>;

interface LocationInfoStorableSchema extends LocationViewQueryStorableSchema {
  registryId: string;
}

interface LocationInfoParentStorableSchema extends LocationViewQueryParentStorableSchema {
  registryId: string;
}

interface LocationInfoParentStorable extends LocationViewQueryParentStorable {
  storableSchema: LocationInfoParentStorableSchema;
}

export interface LocationInfo extends Omit<LocationViewQueryLocation, 'barcode' | 'permissions'> {
  barcode: string | null;
  permissions: Permissions & {__typename?: 'PermissionsNode'};
  storableSchema: LocationInfoStorableSchema;
  parentStorable: LocationInfoParentStorable | InaccessibleInfoGraphQL | null;
}

export const LOCATION_VIEW_QUERY = gql`
  query LocationViewQuery($id: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    location(id: $id) {
      ... on LocationNode {
        id
        barcode {
          value
        }
        url # LocationFileState needs this
        name
        permissions {
          readable
          writable
          appendable
          admin
          archivable
          unarchivable
          canEditNamesAndBarcodes
          canEditBarcodes
        }
        tags: tagsJson
        createdAt
        archiveRecord {
          purpose
        }
        parentStorable {
          ... on StorableInterface {
            id
            name
            archiveRecord {
              purpose
            }
            parentStorable {
              ... on StorableInterface {
                id
              }
              ...InaccessibleInfoGraphQL
            }
            storableSchema {
              id
              registry {
                id
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        storableSchema {
          id
          name
          registry {
            id
          }
          archiveRecord {
            purpose
          }
        }
        ancestry {
          ... on StorableInterface {
            id
            name
            storableSchema {
              id
              registry {
                id
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        allowsNonLocationChildren
        allowedChildStorableSchemaApiIds
        capacity
        occupiedCapacity
      }
      ...HasCreatorGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasCreator').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function translateToLocationInfo(location: LocationViewQueryLocation): LocationInfo {
  return {
    ...location,
    barcode: location.barcode?.value ?? null,
    storableSchema: {
      ...location.storableSchema,
      registryId: location.storableSchema.registry.id,
    },
    parentStorable:
      location.parentStorable == null || isInaccessible(location.parentStorable)
        ? location.parentStorable
        : {
            ...location.parentStorable,
            storableSchema: {
              ...location.parentStorable.storableSchema,
              registryId: location.parentStorable.storableSchema.registry.id,
            },
          },
    permissions: {
      ...location.permissions,
      canEditNamesAndBarcodes: location.permissions.canEditNamesAndBarcodes ?? undefined,
      canEditBarcodes: location.permissions.canEditBarcodes ?? undefined,
    },
  };
}

export function translateFromLocationInfo(locationInfo: LocationInfo): LocationViewQueryLocation {
  return {
    ...locationInfo,
    barcode: locationInfo.barcode ? {__typename: 'BarcodeNode', value: locationInfo.barcode} : null,
    permissions: {
      __typename: 'PermissionsNode',
      ...locationInfo.permissions,
      archivable: locationInfo.permissions.archivable ?? null,
      unarchivable: locationInfo.permissions.unarchivable ?? null,
      canEditNamesAndBarcodes: locationInfo.permissions.canEditNamesAndBarcodes ?? null,
      canEditBarcodes: locationInfo.permissions.canEditBarcodes ?? null,
    },
  };
}
